import "@/components/oprema/Oprema.css";
import { useEffect, useRef, useState } from "react";
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageHeader, Select } from "@/components/ui/design-system";
import { PrimopredajaStep } from "@/components/oprema/PrimopredajaStep";
import {
  equipmentApi,
  emptyEvidence,
  evidenceReady,
  equipmentFieldErrors,
  equipmentErrorMessage,
  SIZE_FIELDS,
  type EquipmentProposal,
  type HandoverEvidence,
  type HandoverItem,
} from "@/lib/oprema";
import { dohvatiRadnika, punoIme, type Radnik } from "@/lib/radnici";
import { useDanas } from "@/lib/dan";
import { datum } from "@/lib/format";
import { errorMessage } from "@/lib/api";

interface Selection {
  selected: boolean;
  size: string;
  reason: string;
  other: string;
  returned: boolean;
  mark: string;
}
const quotaUsed = (item: EquipmentProposal) =>
  item.kvota_12mj !== null && item.iskoristeno_12mj >= item.kvota_12mj;
export function EquipmentIssuePage() {
  const [params] = useSearchParams();
  const workerId = params.get("radnik") ?? "";
  const siteId = params.get("gradiliste") ?? "";
  return (
    <EquipmentIssue
      key={`${workerId}:${siteId}`}
      workerId={workerId}
      siteId={siteId}
    />
  );
}
function EquipmentIssue({
  workerId,
  siteId,
}: {
  workerId: string;
  siteId: string;
}) {
  const { can, user } = useAuth();
  const today = useDanas();
  const navigate = useNavigate();
  const location = useLocation();
  const [worker, setWorker] = useState<Radnik | null>(null);
  const [proposals, setProposals] = useState<EquipmentProposal[] | null>(null);
  const [choices, setChoices] = useState<Record<string, Selection>>({});
  const [step, setStep] = useState(1);
  const [fromOffice, setFromOffice] = useState(false);
  const [evidence, setEvidence] = useState<HandoverEvidence>(emptyEvidence);
  const [error, setError] = useState("");
  const [fields, setFields] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [revision, setRevision] = useState(0);
  const running = useRef(false);
  const allowed = can("oprema", "create") && can("oprema", "read");
  const readWorker = can("radnik", "read") || can("radnik.osnovno", "read");
  const canIssueOffice = user?.permissions.some(permission => permission.module === "oprema" && permission.action === "create" && permission.scope === "all") ?? false;
  const canSkipPhoto =
    user?.permissions?.some(
      (permission) =>
        permission.module === "oprema" &&
        permission.action === "update" &&
        permission.scope === "all",
    ) ?? false;
  const from = (location.state as { from?: string } | null)?.from;
  const fallback = siteId
    ? `/gradilisni-spisak/${encodeURIComponent(siteId)}?kartica=oprema`
    : `/zaposlenici/${encodeURIComponent(workerId)}?tab=znr`;
  const returnTo =
    from && /^\/(zaposlenici|gradilisni-spisak|gradilista)\//.test(from)
      ? from
      : fallback;
  useEffect(() => {
    if (!allowed || !workerId) return;
    const controller = new AbortController();
    // Proposal is the only equipment lookup needed for selection. Worker metadata uses existing RBAC.
    void equipmentApi
      .proposal(workerId, siteId || undefined, controller.signal)
      .then((items) => {
        if (controller.signal.aborted) return;
        setProposals(items);
        setChoices(
          Object.fromEntries(
            items.map((item) => [
              item.vrsta_ozo_id,
              {
                selected: item.predlozeno && item.nacin !== "zaliha",
                size: item.velicina ?? "",
                reason: "",
                other: "",
                returned: true,
                mark: "",
              },
            ]),
          ),
        );
      })
      .catch((cause) => {
        if (!controller.signal.aborted)
          setError(errorMessage(cause, "Prijedlog opreme nije dostupan."));
      });
    if (readWorker)
      void dohvatiRadnika(workerId, controller.signal)
        .then((value) => {
          if (!controller.signal.aborted) setWorker(value);
        })
        .catch(() => {
          /* Proposal remains usable when worker metadata is restricted. */
        });
    return () => controller.abort();
  }, [allowed, workerId, siteId, revision, readWorker]);
  const selected = (proposals ?? []).filter(
    (item) => choices[item.vrsta_ozo_id]?.selected,
  );
  const selectionValid =
    selected.length > 0 &&
    selected.every((item) => {
      const choice = choices[item.vrsta_ozo_id]!;
      return (
        (!quotaUsed(item) ||
          (choice.reason &&
            (choice.reason !== "Drugo" || choice.other.trim()))) &&
        (item.nacin !== "zaliha" || fromOffice || (siteId && choice.mark.trim()))
      );
    });
  function change(id: string, patch: Partial<Selection>) {
    setChoices((previous) => ({
      ...previous,
      [id]: { ...previous[id]!, ...patch },
    }));
  }
  async function submit() {
    if (
      running.current ||
      !selectionValid ||
      !evidenceReady(evidence, canSkipPhoto)
    )
      return;
    running.current = true;
    setBusy(true);
    setError("");
    setFields({});
    setProgress(0);
    const items: HandoverItem[] = selected.map((item) => {
      const choice = choices[item.vrsta_ozo_id]!;
      return {
        vrsta_ozo_id: item.vrsta_ozo_id,
        velicina: choice.size.trim() || null,
        kolicina: 1,
        razlog_izvan_kvote: quotaUsed(item)
          ? choice.reason === "Drugo"
            ? choice.other.trim()
            : choice.reason
          : null,
        ...(item.nacin === "potrosna" ? { vratio_staro: choice.returned } : {}),
        ...(fromOffice ? { iz_ureda: true } : item.nacin === "zaliha"
          ? { iz_zalihe: true, oznaka: choice.mark.trim() }
          : {}),
      };
    });
    try {
      const receipt = await equipmentApi.issue(
        workerId,
        {
          vrsta: "izdavanje",
          datum: today,
          gradiliste_id: siteId || undefined,
          stavke: items,
        },
        evidence,
        setProgress,
      );
      navigate(returnTo, {
        replace: true,
        state: { handover: { workerId, receipt } },
      });
    } catch (cause) {
      setError(equipmentErrorMessage(cause, "Izdavanje nije uspjelo."));
      setFields(equipmentFieldErrors(cause));
    } finally {
      running.current = false;
      setBusy(false);
    }
  }
  const missing = proposals?.filter(
    (item) => item.nacin !== "zaliha" && !choices[item.vrsta_ozo_id]?.size,
  );
  if (!allowed) return <p role="alert">Nemate ovlast za izdavanje opreme.</p>;
  if (!workerId)
    return (
      <p role="alert">Odaberite radnika iz kartona ili gradilišnog popisa.</p>
    );
  return (
    <div className="avm-equipment mx-auto max-w-3xl space-y-4 pb-6">
      <PageHeader
        title="Izdaj opremu"
        onBack={() => {
          if (busy) return;
          if (step === 2) {
            setStep(1);
            setEvidence((previous) => ({ ...previous, signature: null }));
          } else navigate(returnTo);
        }}
        meta={
          worker
            ? `${punoIme(worker)} · ${worker.radno_mjesto ?? "Radno mjesto nije upisano"} · ${worker.trenutni_raspored?.brigada ?? "Bez brigade"}`
            : "Primopredaja zaštitne opreme"
        }
      />
      <ol aria-label="Koraci primopredaje" className="space-y-2">
        <li
          className={`rounded border p-3 ${step === 1 ? "border-blue-300 bg-blue-50" : "border-green-300 bg-green-50"}`}
        >
          {step === 2 ? "✓" : "1."} Odabir opreme
        </li>
        <li
          className={`rounded border p-3 ${step === 2 ? "border-blue-300 bg-blue-50" : "bg-slate-100 text-slate-600"}`}
        >
          2. Fotka i potpis
        </li>
      </ol>
      {error && (
        <div role="alert">
          {error}
          {!proposals && (
            <Button
              variant="outline"
              onClick={() => setRevision((value) => value + 1)}
            >
              Pokušaj ponovno
            </Button>
          )}
        </div>
      )}
      {!proposals && !error && <p role="status">Učitavanje prijedloga…</p>}
      {proposals && step === 1 && (
        <>
          {canIssueOffice && <label className="flex min-h-11 items-center gap-3 rounded border p-3"><input type="checkbox" checked={fromOffice} onChange={event => setFromOffice(event.target.checked)} />Iz zalihe ureda (oduzima se tek nakon potpisa)</label>}
          <p>
            {missing?.length ? (
              <>
                Fali veličina: {missing.map((item) => item.naziv).join(", ")}.{" "}
                {can("radnik", "update") && can("znr", "read") ? (
                  <Link
                    className="inline-flex min-h-11 items-center underline"
                    to={`/zaposlenici/${workerId}?tab=znr#velicine`}
                  >
                    Upiši veličine
                  </Link>
                ) : (
                  "Upišite veličinu uz odabranu stavku."
                )}
              </>
            ) : (
              "Veličine upisane ✓"
            )}
          </p>
          <section
            aria-label="Sljedeći korak"
            className="rounded-lg border bg-white p-4"
          >
            <h2 className="font-semibold">Sljedeći korak</h2>
            <p>{selected.length ? "✓" : "✗"} Odaberite opremu za izdavanje.</p>
            <p>
              {selectionValid ? "✓" : "✗"} Upišite razlog za potrošene kvote i
              oznaku čizama iz zalihe.
            </p>
          </section>
          <div className="space-y-3">
            {proposals.map((item) => {
              const choice = choices[item.vrsta_ozo_id];
              if (!choice) return null;
              const stock = item.nacin === "zaliha";
              const taggedBoots =
                stock && /čizm/i.test(item.naziv) && Boolean(siteId);
              return (
                <article
                  key={item.vrsta_ozo_id}
                  className={`space-y-3 rounded-lg border p-4 ${choice.selected ? "border-blue-300 bg-blue-50" : "bg-white"}`}
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <label className="flex min-h-11 flex-1 items-center gap-3 font-semibold">
                      <input
                        type="checkbox"
                        className="size-5"
                        disabled={stock && !taggedBoots && !fromOffice}
                        checked={choice.selected}
                        onChange={(event) =>
                          change(item.vrsta_ozo_id, {
                            selected: event.target.checked,
                          })
                        }
                      />
                      {item.naziv}
                    </label>
                    {!stock || taggedBoots || fromOffice ? (
                      <label className="max-w-32 text-sm">
                        Veličina
                        <Input
                          className="min-h-11"
                          aria-label={`Veličina: ${item.naziv}`}
                          maxLength={40}
                          value={choice.size}
                          onChange={(event) =>
                            change(item.vrsta_ozo_id, {
                              size: event.target.value,
                            })
                          }
                        />
                      </label>
                    ) : null}
                  </div>
                  {item.kvota_12mj !== null && (
                    <p>
                      Dobio {item.iskoristeno_12mj} od {item.kvota_12mj} u
                      zadnjih 12 mj.
                    </p>
                  )}
                  {item.nacin === "potrosna" && (
                    <p>{item.potrosnja_mjesec} ovaj mjesec</p>
                  )}
                  {item.zadnje_izdano && (
                    <p>Zadnje izdano: {datum(item.zadnje_izdano)}</p>
                  )}
                  {item.aktivno_zaduzenje?.rok_zamjene && (
                    <p>
                      Rok zamjene: {datum(item.aktivno_zaduzenje.rok_zamjene)}
                    </p>
                  )}
                  {quotaUsed(item) && (
                    <>
                      <span className="inline-flex rounded-full bg-amber-100 px-3 py-1 text-amber-950">
                        kvota potrošena
                      </span>
                      {choice.selected && (
                        <>
                          <label className="block">
                            Razlog izvan kvote: {item.naziv}
                            <Select
                              className="min-h-11 w-full"
                              value={choice.reason}
                              onChange={(event) =>
                                change(item.vrsta_ozo_id, {
                                  reason: event.target.value,
                                })
                              }
                            >
                              <option value="">Odaberite razlog</option>
                              {[
                                "Oštećeno na radu",
                                "Izgubljeno",
                                "Novi radnik",
                                "Drugo",
                              ].map((reason) => (
                                <option key={reason}>{reason}</option>
                              ))}
                            </Select>
                          </label>
                          {choice.reason === "Drugo" && (
                            <label className="block">
                              Drugi razlog: {item.naziv}
                              <Input
                                className="min-h-11"
                                value={choice.other}
                                maxLength={2000}
                                onChange={(event) =>
                                  change(item.vrsta_ozo_id, {
                                    other: event.target.value,
                                  })
                                }
                              />
                            </label>
                          )}
                        </>
                      )}
                    </>
                  )}
                  {item.nacin === "potrosna" && choice.selected && (
                    <label className="flex min-h-11 items-center gap-3">
                      <input
                        type="checkbox"
                        checked={choice.returned}
                        onChange={(event) =>
                          change(item.vrsta_ozo_id, {
                            returned: event.target.checked,
                          })
                        }
                      />
                      Vratio stare (slikaj)
                    </label>
                  )}
                  {stock && (
                    <>
                      <p>
                        Zaliha gradilišta: {item.zaliha_gradilista ?? 0} kom
                      </p>
                      {taggedBoots && choice.selected ? (
                        <label className="block">
                          Oznaka čizama (obavezno)
                          <Input
                            className="min-h-11"
                            maxLength={80}
                            value={choice.mark}
                            onChange={(event) =>
                              change(item.vrsta_ozo_id, {
                                mark: event.target.value,
                              })
                            }
                          />
                        </label>
                      ) : (
                        <p>Izdaje se preko kartice Oprema na gradilištu.</p>
                      )}
                    </>
                  )}
                </article>
              );
            })}
          </div>
          <Button
            className="min-h-12 w-full"
            disabled={!selectionValid}
            onClick={() => {
              setStep(2);
              setError("");
            }}
          >
            Dalje: fotka i potpis ({selected.length} stavki)
          </Button>
        </>
      )}
      {step === 2 && (
        <>
          <p>
            {worker ? punoIme(worker) : "Radnik"} preuzima:{" "}
            {selected
              .map(
                (item) =>
                  `${item.naziv}${choices[item.vrsta_ozo_id]?.size ? ` (${choices[item.vrsta_ozo_id]!.size})` : ""}`,
              )
              .join(", ")}
            .
          </p>
          <PrimopredajaStep
            value={evidence}
            onChange={setEvidence}
            canSkipPhoto={canSkipPhoto}
            disabled={busy}
            errors={fields}
            meta={`${datum(today)} · ${new Intl.DateTimeFormat("hr-HR", { timeZone: "Europe/Zagreb", hour: "2-digit", minute: "2-digit" }).format(new Date())} · ${worker?.trenutni_raspored?.gradiliste_id === siteId ? worker.trenutni_raspored.gradiliste : siteId ? "Odabrano gradilište" : "Bez gradilišta"} · Izdao: ${user?.full_name ?? "Prijavljeni korisnik"}`}
          />
          {Object.entries(fields)
            .filter(
              ([field]) =>
                !["potpis", "fotke[]", "bez_fotke_razlog"].includes(field),
            )
            .map(([field, message]) => (
              <p role="alert" key={field}>
                {field.startsWith("stavke.")
                  ? `${selected[Number(field.split(".")[1])]?.naziv ?? "Stavka"}: `
                  : `${proposals?.find((item) => field.startsWith(`${item.vrsta_ozo_id}.`))?.naziv ?? SIZE_FIELDS[field as keyof typeof SIZE_FIELDS] ?? "Podatci primopredaje"}: `}
                {message}
              </p>
            ))}
          {busy && (
            <div role="status">
              <label>
                Slanje primopredaje{" "}
                <progress
                  aria-label="Napredak slanja"
                  max={100}
                  value={progress}
                />
              </label>
              <p>{progress >= 100 ? "Izrada potvrde…" : `${progress} %`}</p>
            </div>
          )}
          <Button
            className="min-h-12 w-full"
            disabled={busy || !evidenceReady(evidence, canSkipPhoto)}
            onClick={() => void submit()}
          >
            {busy ? "Izdavanje…" : "Izdaj i spremi potvrdu"}
          </Button>
        </>
      )}
    </div>
  );
}
